import React from 'react';
import { DataTable as PrimeDataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import type { ColumnProps } from 'primereact/column';

// PrimeReact's DataTable is typed as a discriminated union (single vs multiple
// selection), which makes generic prop-spreading fail overload resolution.
// Treat it as a permissive component so callers can pass any valid prop set.
const Table = PrimeDataTable as unknown as React.ComponentType<any>;

export interface DataTableColumn<T = any> extends Omit<ColumnProps, 'body'> {
  field?: string;
  header?: string;
  sortable?: boolean;
  body?: ((rowData: T, options?: any) => React.ReactNode) | React.ReactNode;
  editor?: (options: any) => React.ReactNode;
  onCellEditComplete?: (e: any) => void;
  style?: React.CSSProperties;
  headerStyle?: React.CSSProperties;
  className?: string;
  frozen?: boolean;
  alignFrozen?: 'left' | 'right';
  exportable?: boolean;
  hidden?: boolean;
}

export interface DataTableComponentProps<T = any> {
  data?: T[];
  columns?: DataTableColumn<T>[];
  testId?: string;
  loading?: boolean;
  /** Show the loading overlay while data is being fetched. */
  showSkeleton?: boolean;
  /**
   * Any other PrimeReact DataTable prop. PrimeReact types DataTable as a
   * single-vs-multiple selection discriminated union, which makes strict
   * prop typing here impractical for a generic wrapper.
   */
  [key: string]: any;
}

// Default paginator layout: a "Showing X to Y of Z • Page N of M" report plus the
// usual navigation controls. Applied whenever `paginator` is on; callers can still
// override `paginatorTemplate` / `currentPageReportTemplate` per table.
const DEFAULT_PAGINATOR_TEMPLATE =
  'CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown';
const DEFAULT_CURRENT_PAGE_REPORT_TEMPLATE =
  'Showing {first}–{last} of {totalRecords}  ·  Page {currentPage} of {totalPages}';

export function DataTable<T = any>({
  data,
  columns,
  testId,
  showSkeleton,
  loading,
  children,
  paginatorTemplate,
  currentPageReportTemplate,
  ...rest
}: DataTableComponentProps<T> & { children?: React.ReactNode }) {
  const paginatorEnabled = (rest as any).paginator;
  return (
    <Table
      value={data || (rest as any).value}
      loading={loading || showSkeleton}
      data-testid={testId}
      {...(paginatorEnabled
        ? {
            paginatorTemplate: paginatorTemplate ?? DEFAULT_PAGINATOR_TEMPLATE,
            currentPageReportTemplate:
              currentPageReportTemplate ?? DEFAULT_CURRENT_PAGE_REPORT_TEMPLATE,
          }
        : {})}
      {...(rest as any)}
    >
      {columns?.map((col, index) => (
        <Column key={col.field || index} {...(col as any)} />
      ))}
      {children}
    </Table>
  );
}

export default DataTable;
